import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Coins,
  Calendar,
  Clock,
  Hash,
  CalendarDays,
  Check,
  ChevronDown,
  X
} from 'lucide-react';
import {
  useConfig,
  CURRENCIES,
  DATE_FORMATS,
  TIME_FORMATS,
  AMOUNT_FORMATS,
  WEEK_DAYS
} from '../context/ConfigContext';
import { useTheme } from '../context/ThemeContext';

// Badge color classes
const BADGE_COLORS = {
  green: 'bg-income-50 text-income-700',
  blue: 'bg-primary-50 text-primary-700',
  purple: 'bg-purple-50 text-purple-700',
  amber: 'bg-amber-50 text-amber-700',
};

// Searchable Currency Dropdown Component
function CurrencySelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const isOpenRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isModernUI } = useTheme();

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const selectedCurrency = CURRENCIES.find(c => c.code === value) || CURRENCIES[0];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1280);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filteredCurrencies = useMemo(() => {
    if (!search) return CURRENCIES;
    const searchLower = search.toLowerCase();
    return CURRENCIES.filter(c =>
      c.code.toLowerCase().includes(searchLower) ||
      c.name.toLowerCase().includes(searchLower) ||
      c.symbol.includes(search)
    );
  }, [search]);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  useEffect(() => {
    const handlePopState = () => {
      if (isOpenRef.current) {
        setIsOpen(false);
        setSearch('');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const openDropdown = () => {
    setIsOpen(true);
    if (isMobile) {
      window.history.pushState({ currencyModal: true }, '');
    }
  };

  const handleSelect = (currency) => {
    onChange(currency.code);
    setSearch('');
    if (isMobile && isOpenRef.current) {
      setIsOpen(false);
      window.history.back();
    } else {
      setIsOpen(false);
    }
  };

  const handleClose = () => {
    setSearch('');
    if (isMobile && isOpenRef.current) {
      setIsOpen(false);
      window.history.back();
    } else {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => isOpen ? handleClose() : openDropdown()}
        className="w-full flex items-center justify-between px-4 py-3 bg-white border border-surface-200 rounded-xl text-left hover:border-surface-300 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
      >
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 flex items-center justify-center bg-surface-100 rounded-lg text-lg font-semibold text-surface-700">
            {selectedCurrency.symbol}
          </span>
          <div>
            <p className="font-medium text-surface-900">{selectedCurrency.code}</p>
            <p className="text-sm text-surface-500">{selectedCurrency.name}</p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-surface-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Mobile: Bottom Sheet */}
      {isOpen && isMobile && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/40 z-[99]" onClick={handleClose} />
          {/* Sheet */}
          <div className="fixed inset-x-0 bottom-0 z-[100] bg-white rounded-t-2xl flex flex-col" style={{ maxHeight: '85vh' }}>
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>
            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <h2 className="text-base font-bold text-surface-900">Select Currency</h2>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors text-surface-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Search */}
            <div className="px-4 pb-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search currency..."
                  className="w-full pl-10 pr-10 py-2.5 bg-surface-50 border border-surface-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {/* Currency List */}
            <div className="flex-1 overflow-y-auto pb-6 border-t border-surface-100">
              {filteredCurrencies.length === 0 ? (
                <div className="p-6 text-center text-surface-500 text-sm">
                  No currencies found
                </div>
              ) : (
                filteredCurrencies.map((currency) => (
                  <button
                    key={currency.code}
                    onClick={() => handleSelect(currency)}
                    className={`w-full flex items-center gap-3 px-4 py-3 border-b border-surface-100 active:bg-surface-50 transition-colors ${
                      currency.code === value ? 'bg-surface-50' : ''
                    }`}
                  >
                    <span className="w-10 h-10 flex items-center justify-center bg-surface-100 rounded-lg text-base font-semibold text-surface-700">
                      {currency.symbol}
                    </span>
                    <div className="flex-1 text-left">
                      <p className="font-semibold text-surface-900 text-sm">{currency.code}</p>
                      <p className="text-xs text-surface-500">{currency.name}</p>
                    </div>
                    {currency.code === value && (
                      <Check className="w-5 h-5 text-income-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* Desktop: Dropdown */}
      {isOpen && !isMobile && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-surface-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-3 border-b border-surface-100">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search currency..."
                className="w-full pl-10 pr-4 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-surface-400 hover:text-surface-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredCurrencies.length === 0 ? (
              <div className="p-4 text-center text-surface-500 text-sm">
                No currencies found
              </div>
            ) : (
              filteredCurrencies.map((currency) => (
                <button
                  key={currency.code}
                  onClick={() => handleSelect(currency)}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-50 transition-colors ${
                    currency.code === value ? 'bg-surface-50' : ''
                  }`}
                >
                  <span className="w-8 h-8 flex items-center justify-center bg-surface-100 rounded-lg text-sm font-semibold text-surface-600">
                    {currency.symbol}
                  </span>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-surface-900">{currency.code}</p>
                    <p className="text-xs text-surface-500">{currency.name}</p>
                  </div>
                  {currency.code === value && (
                    <Check className="w-5 h-5 text-income-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Radio Card Component — compact radio dot + label + example + optional badge
function RadioCard({ option, isSelected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all text-left ${
        isSelected
          ? 'border-primary-500 bg-primary-50/50'
          : 'border-surface-200 bg-white hover:border-surface-300'
      }`}
    >
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        isSelected ? 'border-primary-500 bg-primary-500' : 'border-surface-300'
      }`}>
        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-surface-900">{option.label}</p>
        {option.example && (
          <p className="text-[11px] text-surface-500">{option.example}</p>
        )}
      </div>
      {option.badge && (
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md flex-shrink-0 ${BADGE_COLORS[option.badgeColor] || BADGE_COLORS.green}`}>
          {option.badge}
        </span>
      )}
    </button>
  );
}

export default function ConfigurationPage() {
  const { config, updateConfig, saving, formatAmount, formatDate, formatTime } = useConfig();

  // Show bottom toast on config save
  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => {
    const handler = () => {
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1500);
    };
    window.addEventListener('config-saved', handler);
    return () => window.removeEventListener('config-saved', handler);
  }, []);
  const { isModernUI } = useTheme();
  const [showAllDates, setShowAllDates] = useState(false);

  const previewDate = new Date();
  const previewAmount = 123456.78;

  const sectionCard = 'bg-white rounded-2xl border border-surface-100 p-3.5';
  const sectionHeaderGap = 'gap-2.5 mb-2.5';

  return (
    <div className="space-y-3 pb-6">
      {/* Mobile Header */}
      <div className="xl:hidden mb-1">
        <h1 className="text-base font-bold text-surface-900">Configuration</h1>
        <p className="text-[10px] text-surface-500">
          Customize app display settings
        </p>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:block page-header">
        <h1 className="page-title">Configuration</h1>
        <p className="page-subtitle">
          Customize app display settings
        </p>
      </div>

      {/* Currency Section */}
      <div className={sectionCard}>
        <div className={`flex items-center ${sectionHeaderGap}`}>
          <div className="p-2 rounded-xl" style={{ backgroundColor: 'var(--accent-light)' }}>
            <Coins className="w-4 h-4" style={{ color: 'var(--accent-light-text)' }} />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-surface-900">Currency</h2>
            <p className="text-[10px] text-surface-500">Select your preferred currency</p>
          </div>
          <span className="text-sm font-bold text-surface-900">{formatAmount(previewAmount)}</span>
        </div>
        <CurrencySelect
          value={config.currency}
          onChange={(value) => updateConfig('currency', value)}
        />
      </div>

      {/* Date Format Section */}
      <div className={sectionCard}>
        <div className={`flex items-center ${sectionHeaderGap}`}>
          <div className="p-2 rounded-xl bg-amber-50">
            <Calendar className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-surface-900">Date Format</h2>
            <p className="text-[10px] text-surface-500">How dates appear across the app</p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-surface-400">Preview</p>
            <p className="text-sm font-bold text-surface-900">{formatDate(previewDate)}</p>
          </div>
        </div>
        <div className="space-y-1.5">
          {(showAllDates ? DATE_FORMATS : DATE_FORMATS.slice(0, 5)).map((option) => (
            <RadioCard
              key={option.value}
              option={option}
              isSelected={config.dateFormat === option.value}
              onClick={() => updateConfig('dateFormat', option.value)}
            />
          ))}
          {DATE_FORMATS.length > 5 && (
            <button
              onClick={() => setShowAllDates(!showAllDates)}
              className="w-full py-2 text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              {showAllDates ? 'Show less' : `Show ${DATE_FORMATS.length - 5} more formats`}
            </button>
          )}
        </div>
      </div>

      {/* Time Format Section */}
      <div className={sectionCard}>
        <div className={`flex items-center ${sectionHeaderGap}`}>
          <div className="p-2 rounded-xl bg-blue-50">
            <Clock className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-surface-900">Time Format</h2>
            <p className="text-[10px] text-surface-500">12-hour or 24-hour clock</p>
          </div>
          <span className="text-sm font-bold" style={{ color: 'var(--accent-bg)' }}>{formatTime('14:30')}</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {TIME_FORMATS.map((option) => (
            <RadioCard
              key={option.value}
              option={option}
              isSelected={config.timeFormat === option.value}
              onClick={() => updateConfig('timeFormat', option.value)}
            />
          ))}
        </div>
      </div>

      {/* Amount Format Section */}
      <div className={sectionCard}>
        <div className={`flex items-center ${sectionHeaderGap}`}>
          <div className="p-2 rounded-xl bg-purple-50">
            <Hash className="w-4 h-4 text-purple-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-surface-900">Amount Format</h2>
            <p className="text-[10px] text-surface-500">Number grouping style</p>
          </div>
          <span className="text-sm font-bold text-surface-900">{formatAmount(100000, true, true)}</span>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {AMOUNT_FORMATS.map((option) => (
            <RadioCard
              key={option.value}
              option={option}
              isSelected={config.amountFormat === option.value}
              onClick={() => updateConfig('amountFormat', option.value)}
            />
          ))}
        </div>
      </div>

      {/* Week Start Day Section */}
      <div className={sectionCard}>
        <div className={`flex items-center ${sectionHeaderGap}`}>
          <div className="p-2 rounded-xl bg-pink-50">
            <CalendarDays className="w-4 h-4 text-pink-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-surface-900">Week Start Day</h2>
            <p className="text-[10px] text-surface-500">First day shown in the calendar</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {WEEK_DAYS.map((day) => (
            <button
              key={day.value}
              onClick={() => updateConfig('weekStartDay', day.value)}
              className={`px-3.5 py-2 rounded-full text-xs font-semibold transition-all ${
                config.weekStartDay === day.value
                  ? 'text-white'
                  : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
              }`}
              style={config.weekStartDay === day.value ? {
                backgroundColor: 'var(--accent-bg)',
                color: 'var(--accent-text)'
              } : {}}
            >
              {day.label}
            </button>
          ))}
        </div>
        {/* Calendar Preview */}
        <div className="mt-3 p-2.5 bg-surface-50 rounded-xl">
          <p className="text-[10px] text-surface-400 mb-1.5">Calendar preview</p>
          <div className="flex justify-center gap-1 mb-1">
            {[...Array(7)].map((_, i) => {
              const dayIndex = (config.weekStartDay + i) % 7;
              const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
              const isSun = dayIndex === 0;
              const isSat = dayIndex === 6;
              return (
                <div
                  key={`h-${i}`}
                  className={`w-8 h-6 flex items-center justify-center text-[10px] font-semibold ${
                    isSun ? 'text-expense-500' : isSat ? 'text-primary-500' : 'text-surface-400'
                  }`}
                >
                  {dayNames[dayIndex]}
                </div>
              );
            })}
          </div>
          <div className="flex justify-center gap-1">
            {[...Array(7)].map((_, i) => (
              <div
                key={`d-${i}`}
                className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold ${
                  i === 0 ? 'text-white' : 'bg-white text-surface-600'
                }`}
                style={i === 0 ? { backgroundColor: 'var(--accent-bg)' } : {}}
              >
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom toast — auto-saved confirmation */}
      {showSaved && (
        <div className="fixed bottom-24 xl:bottom-6 left-0 right-0 flex justify-center z-[200] pointer-events-none animate-slide-up">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-2xl shadow-xl" style={{ backgroundColor: '#10b981f0', boxShadow: '0 8px 32px #10b98140' }}>
            <Check className="w-4 h-4 text-white" />
            <span className="text-[13px] font-semibold text-white">Saved</span>
          </div>
        </div>
      )}
    </div>
  );
}
